import React, { useState, useEffect } from 'react';
import {
  Flex,
  Typography,
  Button,
  Card,
  Table,
  Tag,
  Avatar,
  Tabs,
  Spin,
  Skeleton,
  Row,
  Col,
  Segmented,
  Space
} from 'antd';
import {
  ArrowLeftOutlined,
  TeamOutlined,
  CarOutlined,
  RiseOutlined,
  BankOutlined,
  UserOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardService from '../../service';
import LotCard from '../../components/LotCard';
import VinCopy from '../../components/VinCopy';
import { useNotification } from '../../hooks/useNotification';
import CreatorInfo from '../../components/CreatorInfo';

const DealerDetail = () => {
  const { dealerId, lang } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [dealer, setDealer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subdealers, setSubdealers] = useState([]);
  const [purchasedCars, setPurchasedCars] = useState([]);
  const [activeTab, setActiveTab] = useState('0');
  const [winCars, setWinCars] = useState([]);
  const [bids, setBids] = useState([]);
  const [bidViewMode, setBidViewMode] = useState('grouped');
  const [groupedBids, setGroupedBids] = useState([]);
  const [isBidsGrouped, setIsBidsGrouped] = useState(false);
  const [dealerStats, setDealerStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    const loadDealerData = async () => {
      try {
        setLoading(true);
        const response = await DashboardService.getUserById(dealerId);
        if (response.success && response.user) {
          const userData = response.user;
          if (!userData.branchName && userData.branchId) {
            try {
              const branchRes = await DashboardService.getBranchById(userData.branchId);
              if (branchRes?.success && branchRes?.branch?.name) {
                userData.branchName = branchRes.branch.name;
              }
            } catch (e) {
              // ignore branch lookup failure, keep fallback
            }
          }
          setDealer(userData);

          const subdealersResponse = await DashboardService.getDealerSubdealers(dealerId);
          if (subdealersResponse.success && subdealersResponse.users) {
            setSubdealers(subdealersResponse.users);
          }

          try {
            const purchasedLotsResponse = await DashboardService.getDealerPurchasedLots(dealerId, { page: 1, limit: 1000 });
            if (purchasedLotsResponse?.success && purchasedLotsResponse.lots) {
              setPurchasedCars(purchasedLotsResponse.lots);
            } else if (purchasedLotsResponse?.lots) {
              setPurchasedCars(purchasedLotsResponse.lots);
            }
          } catch (error) {
            console.error('Error loading purchased lots:', error);
          }

          loadDealerStats();
        }
      } catch (error) {
        console.error('Error loading dealer data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (dealerId) {
      loadDealerData();
    }
  }, [dealerId]);

  const loadDealerStats = async () => {
    if (!dealerId) return;
    try {
      setStatsLoading(true);
      const dashboardResponse = await DashboardService.getDealerDashboard(dealerId, dealerId);
      if (dashboardResponse.success && dashboardResponse.dashboard) {
        setDealerStats(dashboardResponse.dashboard.overview);
        if (dashboardResponse.dashboard.recentActivity?.allWins) {
          setWinCars(dashboardResponse.dashboard.recentActivity.allWins);
        }
      }
    } catch (error) {
      console.log('Dashboard endpoint not available, trying stats endpoint');
      try {
        const statsResponse = await DashboardService.getBidStats('dealer', dealerId);
        if (statsResponse.success) {
          setDealerStats(statsResponse.stats);
        }
      } catch (err) {
        console.error('Error loading dealer stats:', err);
      }
    } finally {
      setStatsLoading(false);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(`/${lang}/dealers`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'won': return 'success';
      case 'active': return 'blue';
      case 'lost': return 'error';
      default: return 'default';
    }
  };

  const handleDeleteSubdealer = async (subdealer, e) => {
    e.stopPropagation();

    if (!window.confirm(`Are you sure you want to delete subdealer "${subdealer.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const subdealerId = subdealer.user_id;
      const result = await DashboardService.deleteUser(subdealerId);

      if (result.success) {
        showSuccess('Subdealer deleted successfully!');
        const subdealersResponse = await DashboardService.getDealerSubdealers(dealerId);
        if (subdealersResponse.success && subdealersResponse.users) {
          setSubdealers(subdealersResponse.users);
        }
      } else {
        showError(result.error || 'Failed to delete subdealer');
      }
    } catch (error) {
      console.error('Error deleting subdealer:', error);
      showError(error.message || 'Failed to delete subdealer. Please try again.');
    }
  };

  const loadDealerBids = async () => {
    if (!dealerId) return;
    try {
      const groupByLot = bidViewMode === 'grouped';
      const response = await DashboardService.getDealerBids(dealerId, {
        page: 1,
        limit: 50,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        groupByLot
      });

      if (response.success !== false) {
        const bidsData = response.lots || response.bids || [];
        setGroupedBids(bidsData);
        setIsBidsGrouped(response.grouped || false);
      }
    } catch (error) {
      console.error('Error loading dealer bids:', error);
      setGroupedBids([]);
    }
  };

  useEffect(() => {
    if (activeTab === '3' && dealerId) {
      loadDealerBids();
    }
  }, [bidViewMode, activeTab, dealerId]);

  useEffect(() => {
    if (activeTab === '4' && dealerId) {
      loadWinCars();
    }
  }, [activeTab, dealerId]);

  const loadWinCars = async () => {
    if (winCars.length > 0) return;
    if (!dealerId) return;
    try {
      const response = await DashboardService.getUserWinningBids(dealerId);
      if (response.success && response.winningLots) {
        setWinCars(response.winningLots);
      } else if (response.winningLots) {
        setWinCars(response.winningLots);
      }
    } catch (error) {
      console.error('Error loading winning lots:', error);
      setWinCars([]);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 2 }} style={{ marginBottom: 24 }} />
        <Flex gap={16} style={{ marginBottom: 24 }}>
          <Skeleton.Avatar active size={64} />
          <div style={{ flex: 1 }}>
            <Skeleton.Input active block style={{ marginBottom: 8 }} />
            <Skeleton.Input active block size="small" />
          </div>
        </Flex>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {[1, 2, 3, 4].map((i) => (
            <Col xs={24} sm={12} md={6} key={i}>
              <Card><Skeleton active /></Card>
            </Col>
          ))}
        </Row>
        <Skeleton.Button active block style={{ marginBottom: 16 }} />
        <Card><Skeleton active paragraph={{ rows: 5 }} /></Card>
      </div>
    );
  }

  if (!dealer) {
    return (
      <div style={{ padding: 24, textAlign: 'center', marginTop: 48 }}>
        <Typography.Title level={4} type="danger">Dealer not found</Typography.Title>
        <Button type="primary" icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back to Dealers
        </Button>
      </div>
    );
  }

  const branchName = dealer.branchName || 'Unknown Branch';
  const dealerBids = bids.filter(b => b.dealerBid || b.userId === dealer?.user_id);
  const subdealerBids = bids.filter(b =>
    b.subdealerId && subdealers.some(s => s.id === b.subdealerId)
  );

  const stats = [
    {
      title: 'Total Subdealers',
      value: dealerStats?.totalSubdealers || subdealers.length,
      icon: <TeamOutlined />,
      color: '#2196f3'
    },
    {
      title: 'Total Bids',
      value: dealerStats?.totalBids || groupedBids.length,
      icon: <RiseOutlined />,
      color: '#4caf50',
      subtitle: dealerStats ? `Dealer: ${dealerStats.dealerBids || 0} | Subs: ${dealerStats.subdealerBids || 0}` : ''
    },
    {
      title: 'Won Auctions',
      value: dealerStats?.wonAuctions || 0,
      icon: <CarOutlined />,
      color: '#9c27b0',
      subtitle: dealerStats?.successRate ? `${dealerStats.successRate.toFixed(1)}% success` : ''
    },
    {
      title: 'Purchased Cars',
      value: dealerStats?.purchasedCars?.combined?.count || purchasedCars.length,
      icon: <CarOutlined />,
      color: '#ff9800',
      subtitle: dealerStats?.purchasedCars?.combined?.totalAmount
        ? `$${dealerStats.purchasedCars.combined.totalAmount.toLocaleString()}`
        : ''
    }
  ];

  const subdealerColumns = [
    {
      title: 'Subdealer',
      key: 'subdealer',
      render: (_, record) => (
        <Flex align="center" gap={8}>
          <Avatar style={{ backgroundColor: '#8b5cf6' }}>{record.avatar}</Avatar>
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>@{record.user_name}</Typography.Text>
          </div>
        </Flex>
      )
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div>{record.phone}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Typography.Text>
        </div>
      )
    },
    {
      title: 'Active Bids',
      dataIndex: ['stats', 'activeBids'],
      key: 'activeBids',
      render: (v) => v || 0
    },
    {
      title: 'Won Auctions',
      dataIndex: ['stats', 'wonAuctions'],
      key: 'wonAuctions',
      render: (v) => v || 0
    },
    {
      title: 'Total Spent',
      dataIndex: ['stats', 'totalSpent'],
      key: 'totalSpent',
      render: (v) => <span style={{ fontWeight: 600 }}>{v || '$0'}</span>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={(e) => handleDeleteSubdealer(record, e)}
          title="Delete Subdealer"
        />
      )
    }
  ];

  const dealerBidsColumns = [
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      key: 'lotNumber',
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>
    },
    {
      title: 'VIN',
      key: 'vin',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <Typography.Text type="secondary">{record.vin || 'N/A'}</Typography.Text>
          {record.vin && <VinCopy vin={record.vin} size="small" />}
        </Flex>
      )
    },
    {
      title: 'Vehicle',
      key: 'vehicle',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <CarOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
          <span>{record.year} {record.make} {record.model}</span>
        </Flex>
      )
    },
    {
      title: 'Bid Amount',
      key: 'bidAmount',
      render: (_, record) => (
        <span style={{ fontWeight: 600, color: '#1e3a5f' }}>
          ${(record.bidAmount || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v) => v ? <Tag color={getStatusColor(v)}>{v.toUpperCase()}</Tag> : null
    }
  ];

  const subdealerBidsColumns = [
    ...dealerBidsColumns.slice(0, -1),
    {
      title: 'Subdealer',
      key: 'subdealer',
      render: (_, record) => {
        const subdealer = subdealers.find(s => s.id === record.subdealerId);
        return subdealer?.name || 'Unknown';
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v) => v ? <Tag color={getStatusColor(v)}>{v.toUpperCase()}</Tag> : null
    }
  ];

  const allBidsColumns = [
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      key: 'lotNumber',
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>
    },
    {
      title: 'VIN',
      key: 'vin',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <Typography.Text type="secondary">{record.vin || 'N/A'}</Typography.Text>
          {record.vin && <VinCopy vin={record.vin} size="small" />}
        </Flex>
      )
    },
    {
      title: 'Bid Amount',
      key: 'bidAmount',
      render: (_, record) => (
        <span style={{ fontWeight: 600, color: '#1e3a5f' }}>
          ${((record.bidAmount || record.bid) || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: 'User',
      dataIndex: 'appUserName',
      key: 'user',
      render: (v) => v || 'Unknown'
    },
    {
      title: 'Event',
      dataIndex: 'event',
      key: 'event',
      render: (v) => v ? <Tag>{v}</Tag> : 'N/A'
    },
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'time',
      render: (v) => v ? new Date(v).toLocaleString() : 'N/A'
    }
  ];

  const winCarsColumns = [
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      key: 'lotNumber',
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>
    },
    {
      title: 'VIN',
      key: 'vin',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <Typography.Text type="secondary">{record.vin || 'N/A'}</Typography.Text>
          {record.vin && <VinCopy vin={record.vin} size="small" />}
        </Flex>
      )
    },
    {
      title: 'Host',
      dataIndex: 'host',
      key: 'host',
      render: (v) => v ? <Tag color="blue">{v}</Tag> : 'N/A'
    },
    {
      title: 'Your Bid',
      key: 'currentBid',
      render: (_, record) => (
        <span style={{ fontWeight: 600, color: '#22c55e' }}>
          ${(record.bidAmount || record.bid || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: 'Bidder',
      key: 'bidder',
      render: (_, record) => (
        <div>
          <div>{record.appUserName || 'N/A'}</div>
          {record.buyerNumber && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>#{record.buyerNumber}</Typography.Text>
          )}
        </div>
      )
    },
    {
      title: 'Last Update',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (v) => v ? new Date(v).toLocaleString() : 'N/A'
    },
    {
      title: 'Times Updated',
      dataIndex: 'updatedCount',
      key: 'updatedCount',
      render: (v) => (
        <Tag color={v > 5 ? 'warning' : v > 0 ? 'blue' : 'default'} title={`Bid updated ${v || 0} times`}>
          {v || 0}
        </Tag>
      )
    }
  ];

  const purchasedColumns = [
    {
      title: 'Image',
      key: 'image',
      render: (_, record) => {
        const carImage = record.images?.length > 0 ? record.images[0].url : null;
        return (
          <div style={{ width: 80, height: 60, position: 'relative' }}>
            {carImage && (
              <img
                src={carImage}
                alt={`${record.year} ${record.make} ${record.model}`}
                style={{
                  width: 80,
                  height: 60,
                  objectFit: 'cover',
                  borderRadius: 4,
                  border: '1px solid #d9d9d9'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                }}
              />
            )}
            <div
              style={{
                width: 80,
                height: 60,
                display: carImage ? 'none' : 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5',
                borderRadius: 4,
                border: '1px solid #d9d9d9'
              }}
            >
              <CarOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />
            </div>
          </div>
        );
      }
    },
    {
      title: 'Lot Number',
      key: 'lotNumber',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.lotNumber}</div>
          {record.gatePassPin && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>PIN: {record.gatePassPin}</Typography.Text>
          )}
        </div>
      )
    },
    {
      title: 'Vehicle',
      key: 'vehicle',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.year} {record.make} {record.model}</div>
          {record.titleStatus && <Tag>{record.titleStatus}</Tag>}
        </div>
      )
    },
    {
      title: 'VIN',
      key: 'vin',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <Typography.Text type="secondary">{record.vin || 'N/A'}</Typography.Text>
          {record.vin && <VinCopy vin={record.vin} size="small" />}
        </Flex>
      )
    },
    {
      title: 'Purchase Price',
      key: 'purchasePrice',
      render: (_, record) => (
        <span style={{ fontWeight: 600 }}>
          ${(record.purchasePriceInDollars || record.purchasePrice || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: 'Fees',
      key: 'fees',
      render: (_, record) => (
        <span>
          ${(record.purchasePriceFeeInDollars || record.purchasePriceFee || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: 'Total',
      key: 'total',
      render: (_, record) => {
        const total = (record.purchasePriceInDollars || record.purchasePrice || 0) + (record.purchasePriceFeeInDollars || record.purchasePriceFee || 0);
        return <span style={{ fontWeight: 700, color: '#1e3a5f' }}>${total.toLocaleString()}</span>;
      }
    },
    {
      title: 'Unpaid',
      key: 'unpaid',
      render: (_, record) => {
        const unpaid = record.unpaidAmountInDollars || record.unpaidAmount || 0;
        return (
          <span style={{ fontWeight: 700, color: unpaid > 0 ? '#ff4d4f' : '#22c55e' }}>
            ${unpaid.toLocaleString()}
          </span>
        );
      }
    },
    {
      title: 'Buyer',
      key: 'buyer',
      render: (_, record) => record.userName || record.appUserName || 'N/A'
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (v) => v || 'N/A'
    }
  ];

  const tabItems = [
    {
      key: '0',
      label: `Subdealers (${subdealers.length})`,
      children: (
        <Table
          columns={subdealerColumns}
          dataSource={subdealers}
          rowKey={(r) => r.user_id || r.id}
          pagination={false}
          locale={{ emptyText: 'No subdealers found' }}
        />
      )
    },
    {
      key: '1',
      label: `My Bids (${dealerStats?.dealerBids || 0})`,
      children: (
        <Table
          columns={dealerBidsColumns}
          dataSource={dealerBids}
          rowKey={(r) => r.id}
          pagination={false}
          locale={{ emptyText: 'No bids found' }}
        />
      )
    },
    {
      key: '2',
      label: `Subdealers Bids (${dealerStats?.subdealerBids || 0})`,
      children: (
        <Table
          columns={subdealerBidsColumns}
          dataSource={subdealerBids}
          rowKey={(r) => r.id}
          pagination={false}
          locale={{ emptyText: 'No bids found' }}
        />
      )
    },
    {
      key: '3',
      label: `All Bids (${dealerStats?.totalBids || groupedBids.length})`,
      children: (
        <div>
          <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>All Bids</Typography.Title>
            <Segmented
              value={bidViewMode}
              onChange={setBidViewMode}
              options={[
                { value: 'grouped', label: <><AppstoreOutlined /> Grouped by Lot</> },
                { value: 'flat', label: <><UnorderedListOutlined /> Flat List</> }
              ]}
            />
          </Flex>

          {isBidsGrouped && bidViewMode === 'grouped' ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              {groupedBids.length > 0 ? (
                groupedBids.map((lot, index) => (
                  <LotCard key={lot.lotNumber || index} lot={lot} />
                ))
              ) : (
                <Card>
                  <Typography.Text type="secondary">No bids found</Typography.Text>
                </Card>
              )}
            </Space>
          ) : (
            <Table
              columns={allBidsColumns}
              dataSource={groupedBids}
              rowKey={(r, i) => r._id || i}
              pagination={false}
              locale={{ emptyText: 'No bids found' }}
            />
          )}
        </div>
      )
    },
    {
      key: '4',
      label: `Winning Lots (${winCars.length})`,
      children: (
        <div>
          <Typography.Title level={5} style={{ marginBottom: 8 }}>Currently Winning Lots</Typography.Title>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            These are auctions where you're currently the highest bidder.
          </Typography.Text>
          <Table
            columns={winCarsColumns}
            dataSource={winCars}
            rowKey={(r) => r._id || r.lotNumber}
            pagination={false}
            locale={{
              emptyText: 'No winning lots yet. Winning lots are auctions where you\'re currently the highest bidder.'
            }}
          />
        </div>
      )
    },
    {
      key: '5',
      label: `Purchased Cars (${purchasedCars.length})`,
      children: (
        <div>
          <Typography.Title level={5} style={{ marginBottom: 8 }}>Purchased Cars</Typography.Title>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            These are cars you've actually won. Auctions are complete and payment is required.
          </Typography.Text>
          <Table
            columns={purchasedColumns}
            dataSource={purchasedCars}
            rowKey={(r) => r._id || r.lotNumber}
            pagination={false}
            locale={{
              emptyText: 'No purchased cars yet. Purchased cars are vehicles you\'ve won at auction and need to pay for.'
            }}
          />
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ marginBottom: 16 }}>
          Back to Dealers
        </Button>

        <Flex align="center" gap={16} style={{ marginBottom: 16 }}>
          <Avatar
            size={64}
            shape="circle"
            style={{ backgroundColor: '#1e3a5f', fontSize: 24, flexShrink: 0 }}
          >
            {dealer.avatar || dealer.name?.substring(0, 2).toUpperCase() || 'D'}
          </Avatar>
          <div>
            <Typography.Title level={3} style={{ margin: 0 }}>{dealer.name}</Typography.Title>
            <Flex align="center" gap={8} wrap="wrap" style={{ marginTop: 4 }}>
              <Tag icon={<BankOutlined />} color="blue">{branchName}</Tag>
              <Tag icon={<UserOutlined />} color="purple">{dealer.managerName}</Tag>
              <Typography.Text type="secondary">@{dealer.user_name}</Typography.Text>
            </Flex>
          </div>
        </Flex>

        <Flex gap={16} style={{ marginBottom: 8 }}>
          <Typography.Text type="secondary">📧 {dealer.email}</Typography.Text>
          <Typography.Text type="secondary">📱 {dealer.phone}</Typography.Text>
        </Flex>

        {(dealer.createdBy || dealer.createdAt) && (
          <div style={{ marginTop: 16 }}>
            <CreatorInfo createdBy={dealer.createdBy} createdAt={dealer.createdAt} />
          </div>
        )}
      </div>

      {/* Calculator Settings Card */}
      <Card style={{ marginBottom: 24 }}>
        <Typography.Title level={5} style={{ marginBottom: 16 }}>Calculator Settings</Typography.Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Typography.Text type="secondary">Show Calculator</Typography.Text>
            <div style={{ marginTop: 4 }}>
              <Tag color={dealer.show_calculator !== false ? 'success' : 'default'}>
                {dealer.show_calculator !== false ? 'Enabled' : 'Disabled'}
              </Tag>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <Typography.Text type="secondary">Show Discount</Typography.Text>
            <div style={{ marginTop: 4 }}>
              <Tag color={dealer.show_discount ? 'blue' : 'default'}>
                {dealer.show_discount ? 'Visible' : 'Hidden'}
              </Tag>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <Typography.Text type="secondary">Discount %</Typography.Text>
            <div style={{ marginTop: 4, fontWeight: 600 }}>{dealer.discountPercent || 0}%</div>
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {statsLoading ? (
          <Col span={24}>
            <Flex justify="center" align="center" style={{ padding: 48 }}>
              <Spin size="large" />
              <Typography.Text style={{ marginLeft: 16 }}>Loading statistics...</Typography.Text>
            </Flex>
          </Col>
        ) : (
          stats.map((stat, index) => (
            <Col xs={24} sm={12} md={6} key={index}>
              <Card size="small">
                <Flex align="center" gap={8}>
                  <span style={{ color: stat.color, fontSize: 20 }}>{stat.icon}</span>
                  <div>
                    <Typography.Title level={4} style={{ margin: 0 }}>{stat.value}</Typography.Title>
                    <Typography.Text type="secondary">{stat.title}</Typography.Text>
                    {stat.subtitle && (
                      <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                        {stat.subtitle}
                      </Typography.Text>
                    )}
                  </div>
                </Flex>
              </Card>
            </Col>
          ))
        )}
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>
    </div>
  );
};

export default DealerDetail;
